import { useState, useEffect } from 'react';
import { MultiSelect, MultiSelectOption } from './MultiSelect';
import { useTranslation } from 'react-i18next';
import { config } from '../../../../config/env';

interface State {
  id: string;
  code: string;
  name: string;
  region: string | null;
}

interface StateMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  selectedRegions?: string[];
  className?: string;
  disabled?: boolean;
}

export function StateMultiSelect({
  value,
  onChange,
  selectedRegions = [],
  className,
  disabled
}: StateMultiSelectProps) {
  const { t } = useTranslation();
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoading(true);
        const token =
          typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
        const base = String(config.api.baseUrl ?? '').trim().replace(/\/+$/, '');
        const response = await fetch(`${base}/location/states`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch states');
        }

        const data: State[] = await response.json();
        setStates(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch states:', error);
        setStates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, []);

  // Filter states by selected regions if any
  const filteredStates = selectedRegions.length > 0
    ? states.filter(state => {
        const regionMapping: Record<string, string> = {
          'NORTH': 'Norte',
          'NORTHEAST': 'Nordeste',
          'MIDWEST': 'Centro-Oeste',
          'SOUTHEAST': 'Sudeste',
          'SOUTH': 'Sul',
        };

        return selectedRegions.some(region =>
          regionMapping[region] === state.region
        );
      })
    : states;

  const options: MultiSelectOption[] = Array.isArray(filteredStates)
    ? filteredStates.map(state => ({
        value: state.code,
        label: `${state.code} - ${state.name}`,
      }))
    : [];

  return (
    <MultiSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={loading ? t('common.loading') : t('leads.autoTransfer.selectStates')}
      searchPlaceholder={t('leads.autoTransfer.searchStates')}
      className={className}
      disabled={disabled || loading}
    />
  );
}
